import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import Itinerary from './Itinerary';
import { API_URL } from './authService';

// Funciones auxiliares para manejar duraciones ISO 8601 (ej: "PT2H30M")
const parseIsoDuration = (isoDuration) => {
  if (!isoDuration) return { hours: '', minutes: '' };
  const match = isoDuration.match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (match) {
    return {
      hours: match[1] ? match[1] : '',
      minutes: match[2] ? match[2] : ''
    };
  }
  return { hours: '', minutes: '' };
};

const formatIsoDuration = (hours, minutes) => {
  const h = parseInt(hours) || 0;
  const m = parseInt(minutes) || 0;
  if (h === 0 && m === 0) return null;
  let duration = 'PT';
  if (h > 0) duration += `${h}H`;
  if (m > 0) duration += `${m}M`;
  return duration === 'PT' ? null : duration;
};

const toLocalISO = (dateInput) => {
  if (!dateInput) return '';
  const date = new Date(dateInput);
  if (isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const [year, month, day] = dateString.split('-');
  return `${day}/${month}/${year}`;
};

function Bookings({ initialData, onClearInitialData }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingBooking, setViewingBooking] = useState(null);
  const [searchField, setSearchField] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const pageSize = 10;
  const { token } = useAuth();

  // --- Form State ---
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    numberPassengers: '',
    consumerPrice: '',
    typeTrip: '',
    travelCompany: '',
    luggage: '',
    origin: '',
    transfer: false,
    carRental: false,
    excursion: false,
    travelInsurance: false,
    transferComments: '',
    carRentalComments: '',
    excursionComments: '',
    travelInsuranceComments: '',
    bookingComments: '',
    passenger: { id: null },
    budget: { id: null }
  });

  // --- Passenger Search State ---
  const [passengerData, setPassengerData] = useState({ name: '', surname: '', dni: '', phoneNumber: '', email: '' });
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedPassenger, setSelectedPassenger] = useState(null);
  const [currentSearchField, setCurrentSearchField] = useState('');
  const [passengerStatus, setPassengerStatus] = useState(''); // 'existing', 'new', 'conflict', ''
  const [conflictPassenger, setConflictPassenger] = useState(null);

  // --- Additional Passengers State ---
  const [additionalPassengers, setAdditionalPassengers] = useState([]);
  const [additionalPassengerSearchData, setAdditionalPassengerSearchData] = useState({ name: '', surname: '', dni: '' });
  const [additionalPassengerSuggestions, setAdditionalPassengerSuggestions] = useState([]);
  const [showAdditionalPassengerSuggestions, setShowAdditionalPassengerSuggestions] = useState(false);
  const [currentAdditionalSearchField, setCurrentAdditionalSearchField] = useState('');

  // --- Itinerary & Accommodations State ---
  const [bookingItinerary, setBookingItinerary] = useState([]);
  const [bookingAccommodations, setBookingAccommodations] = useState([]);
  const [activeAccSearchIndex, setActiveAccSearchIndex] = useState(null);
  const [availableBookingAccommodations, setAvailableBookingAccommodations] = useState([]);

  // --- Travel Information State ---
  const [bookingTravelInformations, setBookingTravelInformations] = useState([]);

  // --- Payments & Collections State ---
  const [bookingPayments, setBookingPayments] = useState([]);
  const [bookingCollections, setBookingCollections] = useState([]);

  const getPaymentStatus = useCallback((booking) => {
    const payments = booking.bookingPayments || [];
    if (payments.length === 0) return { text: 'Sin Pagos', style: styles.statusBadgeGray };

    const totalToPay = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
    if (totalToPay === 0) return { text: 'Sin Pagos', style: styles.statusBadgeGray };

    const totalPaid = payments
      .filter(p => p.paid)
      .reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

    if (totalPaid >= totalToPay) return { text: 'Pagado', style: styles.statusBadgeGreen };
    if (totalPaid > 0) return { text: 'Pagado Parcial', style: styles.statusBadgeYellow };
    return { text: 'Pendiente', style: styles.statusBadgeRed };
  }, []);

  const getCollectionStatus = useCallback((booking) => {
    const collections = booking.bookingCollections || [];
    const consumerPrice = Number(booking.consumerPrice) || 0;

    if (consumerPrice === 0) return { text: 'N/A', style: styles.statusBadgeGray };

    const totalCollected = collections.reduce((sum, c) => sum + (Number(c.amount) || 0), 0);

    if (totalCollected >= consumerPrice) return { text: 'Cobrado', style: styles.statusBadgeGreen };
    if (totalCollected > 0) return { text: 'Señado', style: styles.statusBadgeYellow };
    return { text: 'A Cobrar', style: styles.statusBadgeRed };
  }, []);

  useEffect(() => {
    if (initialData) {
      setFormData(prev => ({
        ...prev,
        budget: { id: initialData.budgetId },
        passenger: { id: initialData.passenger?.id || null },
        numberPassengers: initialData.numberPassengers || ''
      }));

      if (initialData.passenger) {
        setPassengerData({
          name: initialData.passenger.name || '',
          surname: initialData.passenger.surname || '',
          dni: String(initialData.passenger.dni || ''),
          phoneNumber: initialData.passenger.phoneNumber || '',
          email: initialData.passenger.email || ''
        });
        setSelectedPassenger(initialData.passenger);
        setPassengerStatus('existing');
      }

      if (initialData.itinerary) {
        setBookingItinerary(initialData.itinerary);
      }

      setShowForm(true);
      if (onClearInitialData) onClearInitialData();
    }
  }, [initialData, onClearInitialData]);

  const fetchBookings = useCallback(async (page = 0, sort = 'id,desc', search = '') => {
    if (!token) return;
    setLoading(true);
    try {
      const sortParam = `&sort=${sort}`;
      let searchParam = search ? `&${search}` : '';

      // Handle status search (heavy client-side operation)
      if (search && (search.startsWith('paymentStatus=') || search.startsWith('collectionStatus='))) {
        const [type, status] = search.split('=');
        
        // This is a heavy operation. We fetch all bookings and their financial data to filter by a calculated status.
        const allBookingsRes = await fetch(`${API_URL}/api/bookings?size=2000&sort=id,asc`, { headers: { 'Authorization': `Bearer ${token}` } });
        const allPaymentsRes = await fetch(`${API_URL}/api/booking-payments?size=2000`, { headers: { 'Authorization': `Bearer ${token}` } });
        const allCollectionsRes = await fetch(`${API_URL}/api/booking-collections?size=2000`, { headers: { 'Authorization': `Bearer ${token}` } });

        if (!allBookingsRes.ok || !allPaymentsRes.ok || !allCollectionsRes.ok) {
            throw new Error('Failed to fetch data for status filtering');
        }

        const allBookingsData = await allBookingsRes.json();
        const allPaymentsData = await allPaymentsRes.json();
        const allCollectionsData = await allCollectionsRes.json();

        const paymentsByBookingId = allPaymentsData.reduce((acc, p) => {
            const bookingId = p.booking?.id;
            if (bookingId) {
                if (!acc[bookingId]) acc[bookingId] = [];
                acc[bookingId].push(p);
            }
            return acc;
        }, {});

        const collectionsByBookingId = allCollectionsData.reduce((acc, c) => {
            const bookingId = c.booking?.id;
            if (bookingId) {
                if (!acc[bookingId]) acc[bookingId] = [];
                acc[bookingId].push(c);
            }
            return acc;
        }, {});

        const matchingIds = allBookingsData.filter(booking => {
            const tempBooking = { ...booking, bookingPayments: paymentsByBookingId[booking.id] || [], bookingCollections: collectionsByBookingId[booking.id] || [] };
            if (type === 'paymentStatus') return getPaymentStatus(tempBooking).text === status;
            if (type === 'collectionStatus') return getCollectionStatus(tempBooking).text === status;
            return false;
        }).map(b => b.id);

        if (matchingIds.length > 0) {
            searchParam = `&id.in=${matchingIds.join(',')}`;
        } else {
            setBookings([]); setTotalElements(0); setTotalPages(0); setLoading(false); return;
        }
      }
      // Handle passenger search
      else if (search && (search.includes('passenger.name.contains') || search.includes('passenger.surname.contains'))) {
        const isName = search.includes('passenger.name.contains');
        const value = search.split('=')[1];
        const passengerField = isName ? 'name.contains' : 'surname.contains';
        
        const passResponse = await fetch(`${API_URL}/api/passengers?${passengerField}=${value}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (passResponse.ok) {
          const passengers = await passResponse.json();
          const passengerIds = passengers.map(p => p.id);
          
          if (passengerIds.length > 0) {
            searchParam = `&passengerId.in=${passengerIds.join(',')}`;
          } else {
            setBookings([]); setTotalElements(0); setTotalPages(0); setLoading(false); return;
          }
        }
      }
      const response = await fetch(`${API_URL}/api/bookings?page=${page}&size=${pageSize}${sortParam}${searchParam}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch bookings');
      
      let data = await response.json();
      const totalCount = parseInt(response.headers.get('X-Total-Count') || '0');

      // Cargar datos completos del pasajero para cada reserva
      data = await Promise.all(
        data.map(async (booking) => {
          let passengerDetails = null;
          if (booking.passenger && booking.passenger.id) {
            try {
              const passengerResponse = await fetch(`${API_URL}/api/passengers/${booking.passenger.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (passengerResponse.ok) passengerDetails = await passengerResponse.json();
            } catch (error) { console.error(`Error fetching passenger ${booking.passenger.id}:`, error); }
          }

          let bookingPayments = [];
          let bookingCollections = [];
          if (booking.id) {
            try {
              const paymentsRes = await fetch(`${API_URL}/api/booking-payments?bookingId.equals=${booking.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
              if (paymentsRes.ok) bookingPayments = await paymentsRes.json();

              const collectionsRes = await fetch(`${API_URL}/api/booking-collections?bookingId.equals=${booking.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
              if (collectionsRes.ok) bookingCollections = await collectionsRes.json();
            } catch (error) {
              console.error(`Error fetching financial data for booking ${booking.id}:`, error);
            }
          }

          return { ...booking, passenger: passengerDetails, bookingPayments, bookingCollections };
        })
      );
      
      setBookings(data);
      setTotalElements(totalCount);
      setTotalPages(Math.ceil(totalCount / pageSize));
      setCurrentPage(page);

    } catch (error) {
      console.error('Error fetching bookings:', error);
    } finally {
      setLoading(false);
    }
  }, [token, getCollectionStatus, getPaymentStatus]);

  useEffect(() => {
    const fetchAccommodationsForItinerary = async () => {
      if (!bookingItinerary || bookingItinerary.length === 0 || !token) {
        setAvailableBookingAccommodations([]);
        return;
      }
      
      const destinationIds = [...new Set(bookingItinerary.map(item => item.destinationId).filter(Boolean))];
      
      if (destinationIds.length === 0) {
        setAvailableBookingAccommodations([]);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/acommodations?destinationId.in=${destinationIds.join(',')}&size=1000`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableBookingAccommodations(data);
        } else {
          console.error('Failed to fetch accommodations for booking itinerary');
          setAvailableBookingAccommodations([]);
        }
      } catch (error) {
        console.error('Error fetching accommodations for booking:', error);
        setAvailableBookingAccommodations([]);
      }
    };

    fetchAccommodationsForItinerary();
  }, [bookingItinerary, token]);

  // --- Passenger Search Logic ---
  const searchPassengers = async (field, value) => {
    if (!value || value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    try {
      const fieldMap = { 'name': 'name.contains', 'surname': 'surname.contains', 'dni': 'dni.equals', 'phoneNumber': 'phoneNumber.contains', 'email': 'email.contains' };
      const searchParam = `${fieldMap[field]}=${encodeURIComponent(value)}`;
      const response = await fetch(`${API_URL}/api/passengers?${searchParam}&size=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSuggestions(data);
        setShowSuggestions(data.length > 0);
      }
    } catch (error) { console.error('Error searching passengers:', error); }
  };

  const handlePassengerInputChange = (e) => {
    const { name, value } = e.target;
    setPassengerData(prev => ({ ...prev, [name]: value }));
    setCurrentSearchField(name);
    if (['name', 'surname', 'dni'].includes(name)) {
      setPassengerStatus('');
      setSelectedPassenger(null);
      setConflictPassenger(null);
      setFormData(prev => ({ ...prev, passenger: { id: null } }));
    }
    if (value.length >= 2) searchPassengers(name, value);
    else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePassengerInputBlur = () => setTimeout(() => setShowSuggestions(false), 200);

  const handlePassengerInputFocus = (fieldName) => {
    setCurrentSearchField(fieldName);
    if (passengerData[fieldName]?.length >= 2 && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  const selectSuggestion = (passenger) => {
    setPassengerData({ name: passenger.name || '', surname: passenger.surname || '', dni: String(passenger.dni || ''), phoneNumber: passenger.phoneNumber || '', email: passenger.email || '' });
    setSelectedPassenger(passenger);
    setPassengerStatus('existing');
    setFormData(prev => ({ ...prev, passenger: { id: passenger.id } }));
    setSuggestions([]);
    setShowSuggestions(false);
  };

  // --- Passenger Status Check ---
  const checkExistingPassenger = async () => {
    const { name, surname, dni } = passengerData;
    if (!name?.trim() || !surname?.trim() || !String(dni).trim()) {
      setPassengerStatus('');
      setSelectedPassenger(null);
      setConflictPassenger(null);
      return;
    }
    try {
      const dniResponse = await fetch(`${API_URL}/api/passengers?dni.equals=${encodeURIComponent(String(dni))}&size=1`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (dniResponse.ok) {
        const dniData = await dniResponse.json();
        if (dniData.length > 0) {
          const existing = dniData[0];
          if (existing.name?.toLowerCase().trim() === name.toLowerCase().trim() && existing.surname?.toLowerCase().trim() === surname.toLowerCase().trim()) {
            setSelectedPassenger(existing);
            setPassengerStatus('existing');
            setConflictPassenger(null);
            setFormData(prev => ({ ...prev, passenger: { id: existing.id } }));
          } else {
            setConflictPassenger(existing);
            setPassengerStatus('conflict');
            setFormData(prev => ({ ...prev, passenger: { id: null } }));
          }
          return;
        }
      }
      setSelectedPassenger(null);
      setConflictPassenger(null);
      setPassengerStatus('new');
      setFormData(prev => ({ ...prev, passenger: { id: null } }));
    } catch (error) { console.error('Error checking passenger:', error); }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => checkExistingPassenger(), 500);
    return () => clearTimeout(timeoutId);
  }, [passengerData.name, passengerData.surname, passengerData.dni]);

  // --- Additional Passenger Search & Management ---
  const searchAdditionalPassengers = async (field, value) => {
    if (!value || value.length < 2) {
      setAdditionalPassengerSuggestions([]);
      setShowAdditionalPassengerSuggestions(false);
      return;
    }
    try {
      const fieldMap = { 'name': 'name.contains', 'surname': 'surname.contains', 'dni': 'dni.equals' };
      const searchParam = `${fieldMap[field]}=${encodeURIComponent(value)}`;
      const response = await fetch(`${API_URL}/api/passengers?${searchParam}&size=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAdditionalPassengerSuggestions(data);
        setShowAdditionalPassengerSuggestions(data.length > 0);
      }
    } catch (error) { console.error('Error searching passengers:', error); }
  };

  const handleAdditionalPassengerInputChange = (e) => {
    const { name, value } = e.target;
    setAdditionalPassengerSearchData(prev => ({ ...prev, [name]: value }));
    setCurrentAdditionalSearchField(name);
    if (value.length >= 2) searchAdditionalPassengers(name, value);
    else {
      setAdditionalPassengerSuggestions([]);
      setShowAdditionalPassengerSuggestions(false);
    }
  };

  const handleAdditionalPassengerInputBlur = () => setTimeout(() => setShowAdditionalPassengerSuggestions(false), 200);

  const handleAdditionalPassengerInputFocus = (fieldName) => {
    setCurrentAdditionalSearchField(fieldName);
    if (additionalPassengerSearchData[fieldName]?.length >= 2 && additionalPassengerSuggestions.length > 0) {
      setShowAdditionalPassengerSuggestions(true);
    }
  };

  const addAdditionalPassenger = (passenger) => {
    if (passenger.id === selectedPassenger?.id) {
      alert('Este pasajero ya es el titular de la reserva.');
      return;
    }
    if (additionalPassengers.some(p => p.passenger.id === passenger.id)) {
      alert('Este pasajero ya ha sido agregado.');
      return;
    }
    setAdditionalPassengers(prev => [...prev, { passenger, bookingPassengerGroupId: null }]);
    setAdditionalPassengerSearchData({ name: '', surname: '', dni: '' });
    setAdditionalPassengerSuggestions([]);
    setShowAdditionalPassengerSuggestions(false);
  };

  const removeAdditionalPassenger = (passengerId) => {
    setAdditionalPassengers(prev => prev.filter(p => p.passenger.id !== passengerId));
  };

  const addBookingAccommodation = () => {
    setBookingAccommodations(prev => [...prev, {
      id: null,
      arriveDate: '',
      leaveDate: '',
      accomodationComments: '',
      acommodation: null,
      searchTerm: ''
    }]);
  };

  const removeBookingAccommodation = (index) => {
    setBookingAccommodations(prev => prev.filter((_, i) => i !== index));
  };

  const handleBookingAccommodationChange = (index, field, value) => {
    const updated = [...bookingAccommodations];
    updated[index][field] = value;
    setBookingAccommodations(updated);
  };

  const handleBookingAccommodationSearch = (index, value) => {
    const updated = [...bookingAccommodations];
    updated[index].searchTerm = value;
    updated[index].acommodation = null;
    setBookingAccommodations(updated);
  };

  const selectBookingAccommodation = (index, accommodation) => {
    const updated = [...bookingAccommodations];
    updated[index].acommodation = accommodation;
    updated[index].searchTerm = accommodation.acommodationName;
    setBookingAccommodations(updated);
    setActiveAccSearchIndex(null);
  };

  const syncBookingItinerary = async (bookingId) => {
    const response = await fetch(`${API_URL}/api/booking-itineraries?bookingId.equals=${bookingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
    const existingItems = response.ok ? await response.json() : [];
    const existingIds = existingItems.map(item => item.id);
    const uiIds = bookingItinerary.map(item => item.id).filter(Boolean);

    for (const item of bookingItinerary) {
      const payload = { booking: { id: bookingId }, destination: { id: item.destinationId }, departureDate: item.departureDate || null, returnDate: item.returnDate || null };
      if (item.id && existingIds.includes(item.id)) {
        await fetch(`${API_URL}/api/booking-itineraries/${item.id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ ...payload, id: item.id }) });
      } else {
        await fetch(`${API_URL}/api/booking-itineraries`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }
    }
    for (const id of existingIds) {
      if (!uiIds.includes(id)) await fetch(`${API_URL}/api/booking-itineraries/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    }
  };

  const syncBookingAccommodations = async (bookingId) => {
    const response = await fetch(`${API_URL}/api/booking-acommodations?bookingId.equals=${bookingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
    const existingItems = response.ok ? await response.json() : [];
    const existingIds = existingItems.map(item => item.id);
    const uiIds = bookingAccommodations.map(item => item.id).filter(Boolean);

    for (const item of bookingAccommodations) {
      if (!item.acommodation) continue;
      const payload = { booking: { id: bookingId }, acommodation: { id: item.acommodation.id }, arriveDate: item.arriveDate || null, leaveDate: item.leaveDate || null, accomodationComments: item.accomodationComments };
      if (item.id && existingIds.includes(item.id)) {
        await fetch(`${API_URL}/api/booking-acommodations/${item.id}`, { method: 'PUT', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify({ ...payload, id: item.id }) });
      } else {
        await fetch(`${API_URL}/api/booking-acommodations`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
      }
    }
    for (const id of existingIds) {
      if (!uiIds.includes(id)) await fetch(`${API_URL}/api/booking-acommodations/${id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
    }
  };

  // --- Payments & Collections Management ---
  const addBookingPayment = () => setBookingPayments(prev => [...prev, { id: null, supplier: '', reservationCode: '', amount: '', invoiceNumber: '', expirationDate: '', paid: false }]);
  const removeBookingPayment = (index) => setBookingPayments(prev => prev.filter((_, i) => i !== index));
  const handleBookingPaymentChange = (index, field, value) => {
    const updated = [...bookingPayments];
    updated[index][field] = value;
    setBookingPayments(updated);
  };

  const addBookingCollection = () => setBookingCollections(prev => [...prev, { id: null, type: 'Seño', amount: '', reciptNumber: '', billNumber: '', collectionDate: '' }]);
  const removeBookingCollection = (index) => setBookingCollections(prev => prev.filter((_, i) => i !== index));
  const handleBookingCollectionChange = (index, field, value) => {
    const updated = [...bookingCollections];
    updated[index][field] = value;
    setBookingCollections(updated);
  };

  // --- Travel Information Management ---
  const addBookingTravelInfo = () => setBookingTravelInformations(prev => [...prev, { 
    id: null, 
    travelCompany: '', 
    departureAirport: '', 
    arrivalAirport: '', 
    reservationCode: '', 
    departureTime: '', 
    travelTimeHours: '', 
    travelTimeMinutes: '', 
    travelStopover: false, 
    stopoverDurationHours: '', 
    stopoverDurationMinutes: '', 
    airportChange: false 
  }]);
  const removeBookingTravelInfo = (index) => setBookingTravelInformations(prev => prev.filter((_, i) => i !== index));
  const handleBookingTravelInfoChange = (index, field, value) => {
    const updated = [...bookingTravelInformations];
    if (field === 'departureAirport' || field === 'arrivalAirport') value = value.toUpperCase();
    updated[index][field] = value;
    setBookingTravelInformations(updated);
  };

  const syncRelatedEntities = async (bookingId, items, endpoint) => {
    try {
      const response = await fetch(`${API_URL}/api/${endpoint}?bookingId.equals=${bookingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const existingItems = response.ok ? await response.json() : [];
      const existingIds = existingItems.map(item => item.id);
      const uiIds = items.map(item => item.id).filter(Boolean);
  
      // Create or Update
      for (const item of items) {
        // Extraemos campos de UI y campos antiguos para limpiar el objeto que va al server
        const { 
          id, 
          travelTimeHours, travelTimeMinutes, 
          stopoverDurationHours, stopoverDurationMinutes,
          travelTime: oldTravelTime,
          stopoverDuration: oldStopoverDuration,
          ...data 
        } = item;
        
        if (endpoint === 'booking-travel-informations') {
          data.travelTime = formatIsoDuration(travelTimeHours, travelTimeMinutes);
          // Si no hay escala, forzamos la duración a null
          data.stopoverDuration = data.travelStopover ? formatIsoDuration(stopoverDurationHours, stopoverDurationMinutes) : null;
        }

        // Limpiar el objeto de relaciones anidadas que podrían venir de un fetch previo
        if (data.booking) delete data.booking;
        const payload = { ...data, booking: { id: bookingId } };
        
        // Clean up empty date strings
        Object.keys(payload).forEach(key => {
          if ((key.toLowerCase().includes('date') || key.toLowerCase().includes('time')) && payload[key] === '') {
            payload[key] = null;
          }
          // Format departureTime as UTC ISO string for JHipster
          if (key === 'departureTime' && payload[key]) {
            const d = new Date(payload[key]);
            if (!isNaN(d.getTime())) {
              payload[key] = d.toISOString();
            }
          }
        });

        const method = (id && existingIds.some(eid => String(eid) === String(id))) ? 'PUT' : 'POST';
        const url = method === 'PUT' ? `${API_URL}/api/${endpoint}/${id}` : `${API_URL}/api/${endpoint}`;
        
        const responseRes = await fetch(url, { 
          method, 
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, 
          body: JSON.stringify(method === 'PUT' ? { ...payload, id } : payload) 
        });

        if (!responseRes.ok) {
          const errorText = await responseRes.text();
          throw new Error(`Error en ${endpoint} (${method}): ${errorText}`);
        }
      }
  
      // Delete
      const itemsToDelete = existingItems.filter(item => !uiIds.includes(item.id));
      for (const item of itemsToDelete) {
        await fetch(`${API_URL}/api/${endpoint}/${item.id}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      }
    } catch (error) {
      console.error(`Error syncing ${endpoint}:`, error);
      alert(`Hubo un error al guardar los datos de ${endpoint}.`);
    }
  };

  const syncBookingPayments = (bookingId) => syncRelatedEntities(bookingId, bookingPayments, 'booking-payments');
  const syncBookingCollections = (bookingId) => syncRelatedEntities(bookingId, bookingCollections, 'booking-collections');
  const syncBookingTravelInformations = (bookingId) => syncRelatedEntities(bookingId, bookingTravelInformations, 'booking-travel-informations');

  // --- Form Handling ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === 'checkbox') {
      setFormData({ ...formData, [name]: checked });
    } else if (name === 'numberPassengers' || name === 'consumerPrice') {
      setFormData({ ...formData, [name]: value === '' ? '' : parseFloat(value) });
    } else if (name === 'budgetId') {
      setFormData({ ...formData, budget: { id: value ? parseInt(value) : null } });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const createBooking = async (bookingData) => {
    const response = await fetch(`${API_URL}/api/bookings`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(bookingData)
    });
    if (!response.ok) throw new Error("Failed to create booking");
    return response.json();
  };

  const updateBooking = async (id, bookingData) => {
    const response = await fetch(`${API_URL}/api/bookings/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...bookingData, id })
    });
    if (!response.ok) throw new Error("Failed to update booking");
    return response.json();
  };

  const deleteBooking = async (id) => {
    const response = await fetch(`${API_URL}/api/bookings/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error("Failed to delete booking");
  };

  const syncAdditionalPassengers = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-passenger-groups?bookingId.equals=${bookingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch existing passenger groups');
      const existingGroups = await response.json();
      const existingPassengerIds = existingGroups.map(g => g.passenger.id);

      const uiPassengerIds = additionalPassengers.map(p => p.passenger.id);

      const passengersToAdd = additionalPassengers.filter(p => !existingPassengerIds.includes(p.passenger.id));
      for (const p of passengersToAdd) {
        await fetch(`${API_URL}/api/booking-passenger-groups`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ booking: { id: bookingId }, passenger: { id: p.passenger.id } })
        });
      }

      const groupsToDelete = existingGroups.filter(g => !uiPassengerIds.includes(g.passenger.id));
      for (const g of groupsToDelete) {
        await fetch(`${API_URL}/api/booking-passenger-groups/${g.id}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        });
      }
    } catch (error) {
      console.error('Error syncing additional passengers:', error);
      alert('Hubo un error al guardar los pasajeros adicionales.');
    }
  };

  const createPassengerAPI = async (pData) => {
    const response = await fetch(`${API_URL}/api/passengers`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...pData, sex: null })
    });
    if (!response.ok) throw new Error("Failed to create passenger");
    return response.json();
  };

  const updatePassengerAPI = async (id, pData) => {
    const response = await fetch(`${API_URL}/api/passengers/${id}`, {
      method: 'PUT',
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...pData, id })
    });
    if (!response.ok) throw new Error("Failed to update passenger");
    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (passengerStatus === 'conflict') {
      alert('No se puede guardar la reserva. Existe un conflicto con el DNI del pasajero.');
      return;
    }
    try {
      const bookingPayload = { ...formData };
      // Limpiar budget si es null
      if (!bookingPayload.budget.id) bookingPayload.budget = null;

      let finalPassengerId = formData.passenger.id;
      if (finalPassengerId && selectedPassenger && passengerStatus === 'existing') {
        const infoChanged = (passengerData.phoneNumber || '') !== (selectedPassenger.phoneNumber || '') || (passengerData.email || '') !== (selectedPassenger.email || '');
        if (infoChanged) {
          await updatePassengerAPI(finalPassengerId, { ...selectedPassenger, phoneNumber: passengerData.phoneNumber, email: passengerData.email });
        }
      }
      if (!finalPassengerId && passengerStatus === 'new') {
        const newPassenger = await createPassengerAPI(passengerData);
        finalPassengerId = newPassenger.id;
      }

      if (!finalPassengerId) {
        alert('Por favor, seleccione o complete los datos de un pasajero válido.');
        return;
      }

      let savedBooking;
      if (editingId) {
        savedBooking = await updateBooking(editingId, { ...bookingPayload, passenger: { id: finalPassengerId } });
      } else {
        savedBooking = await createBooking({ ...bookingPayload, passenger: { id: finalPassengerId } });
      }

      if (savedBooking && savedBooking.id) {
        await syncAdditionalPassengers(savedBooking.id);
        await syncBookingItinerary(savedBooking.id);
        await syncBookingAccommodations(savedBooking.id);
        await syncBookingPayments(savedBooking.id);
        await syncBookingCollections(savedBooking.id);
        await syncBookingTravelInformations(savedBooking.id);
      }

      resetForm();
      const sortParam = `${sortField},${sortDirection}`;
      const searchParam = buildSearchParam();
      fetchBookings(0, sortParam, searchParam);
    } catch (error) {
      console.error('Error saving booking:', error);
      alert('Error al guardar la reserva.');
    }
  };

  const fetchAdditionalPassengers = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-passenger-groups?bookingId.equals=${bookingId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch passenger groups');
      const groups = await response.json();
      
      const passengerPromises = groups.map(async (group) => {
        if (group.passenger && group.passenger.id) {
          const passResponse = await fetch(`${API_URL}/api/passengers/${group.passenger.id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (passResponse.ok) {
            const passengerDetails = await passResponse.json();
            return { passenger: passengerDetails, bookingPassengerGroupId: group.id };
          }
        }
        return null;
      });
      const passengers = (await Promise.all(passengerPromises)).filter(Boolean);
      setAdditionalPassengers(passengers);
    } catch (error) {
      console.error('Error fetching additional passengers:', error);
      setAdditionalPassengers([]);
    }
  };

  const fetchBookingItinerary = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-itineraries?bookingId.equals=${bookingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch booking itinerary');
      const itineraryItems = await response.json();
      const fullItinerary = await Promise.all(itineraryItems.map(async item => {
        const destRes = await fetch(`${API_URL}/api/destinations/${item.destination.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        const destination = destRes.ok ? await destRes.json() : { id: item.destination.id, destinationName: 'Desconocido' };
        return {
          id: item.id,
          destinationId: destination.id,
          destinationName: `${destination.destinationName} - ${destination.country}`,
          departureDate: item.departureDate,
          returnDate: item.returnDate,
          numberNights: (item.departureDate && item.returnDate) ? Math.ceil(Math.abs(new Date(item.returnDate) - new Date(item.departureDate)) / (1000 * 60 * 60 * 24)) : 0
        };
      }));
      setBookingItinerary(fullItinerary);
    } catch (error) {
      console.error(error);
      setBookingItinerary([]);
    }
  };

  const fetchBookingAccommodations = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-acommodations?bookingId.equals=${bookingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch booking accommodations');
      const accItems = await response.json();
      const fullAccommodations = await Promise.all(accItems.map(async item => {
        const accRes = await fetch(`${API_URL}/api/acommodations/${item.acommodation.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        const accommodationDetails = accRes.ok ? await accRes.json() : { id: item.acommodation.id, acommodationName: 'Desconocido' };
        return {
          id: item.id,
          arriveDate: item.arriveDate,
          leaveDate: item.leaveDate,
          accomodationComments: item.accomodationComments,
          acommodation: accommodationDetails,
          searchTerm: accommodationDetails.acommodationName
        };
      }));
      setBookingAccommodations(fullAccommodations);
    } catch (error) {
      console.error(error);
      setBookingAccommodations([]);
    }
  };

  const fetchBookingPayments = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-payments?bookingId.equals=${bookingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch payments');
      const data = await response.json();
      setBookingPayments(data);
    } catch (error) {
      console.error(error); setBookingPayments([]);
    }
  };

  const fetchBookingCollections = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-collections?bookingId.equals=${bookingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch collections');
      const data = await response.json();
      setBookingCollections(data);
    } catch (error) {
      console.error(error); setBookingCollections([]);
    }
  };

  const fetchPassengerById = async (id) => {
    try {
      const response = await fetch(`${API_URL}/api/passengers/${id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const p = await response.json();
        setPassengerData({ name: p.name || '', surname: p.surname || '', dni: String(p.dni || ''), phoneNumber: p.phoneNumber || '', email: p.email || '' });
        setSelectedPassenger(p);
        setPassengerStatus('existing');
      }
    } catch (error) { 
      console.error('Error fetching passenger by ID:', error); 
    }
  };

  const fetchBookingTravelInformations = async (bookingId) => {
    try {
      const response = await fetch(`${API_URL}/api/booking-travel-informations?bookingId.equals=${bookingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch travel information');
      const data = await response.json();
      const parsedData = data.map(info => {
        const travelTime = parseIsoDuration(info.travelTime);
        const stopoverTime = parseIsoDuration(info.stopoverDuration);
        return { ...info, travelTimeHours: travelTime.hours, travelTimeMinutes: travelTime.minutes, stopoverDurationHours: stopoverTime.hours, stopoverDurationMinutes: stopoverTime.minutes };
      });
      setBookingTravelInformations(parsedData);
    } catch (error) { console.error(error); setBookingTravelInformations([]); }
  };


  const handleEdit = (booking) => {
    setFormData({
      numberPassengers: booking.numberPassengers || '',
      consumerPrice: booking.consumerPrice || '',
      typeTrip: booking.typeTrip || '',
      travelCompany: booking.travelCompany || '',
      luggage: booking.luggage || '',
      origin: booking.origin || '',
      transfer: booking.transfer || false,
      carRental: booking.carRental || false,
      excursion: booking.excursion || false,
      travelInsurance: booking.travelInsurance || false,
      transferComments: booking.transferComments || '',
      carRentalComments: booking.carRentalComments || '',
      excursionComments: booking.excursionComments || '',
      travelInsuranceComments: booking.travelInsuranceComments || '',
      bookingComments: booking.bookingComments || '',
      passenger: { id: booking.passenger?.id || null },
      budget: { id: booking.budget?.id || null }
    });
    if (booking.passenger && booking.passenger.id) {
      fetchPassengerById(booking.passenger.id);
    }

    setEditingId(booking.id);
    setShowForm(true);
    fetchAdditionalPassengers(booking.id);
    fetchBookingItinerary(booking.id);
    fetchBookingAccommodations(booking.id);
    fetchBookingPayments(booking.id);
    fetchBookingCollections(booking.id);
    fetchBookingTravelInformations(booking.id);
  };

  const handleDelete = (id) => {
    setBookingToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (bookingToDelete) {
      try {
        await deleteBooking(bookingToDelete);
        setShowDeleteModal(false);
        setBookingToDelete(null);
        const sortParam = `${sortField},${sortDirection}`;
        const searchParam = buildSearchParam();
        fetchBookings(currentPage, sortParam, searchParam);
      } catch (error) { console.error('Error deleting booking:', error); }
    }
  };

  const resetForm = () => {
    setFormData({
      numberPassengers: '', consumerPrice: '', typeTrip: '', travelCompany: '', luggage: '', origin: '',
      transfer: false, carRental: false, excursion: false, travelInsurance: false,
      transferComments: '', carRentalComments: '', excursionComments: '', travelInsuranceComments: '',
      bookingComments: '', passenger: { id: null }, budget: { id: null }
    });
    setPassengerData({ name: '', surname: '', dni: '', phoneNumber: '', email: '' });
    setSuggestions([]);
    setShowSuggestions(false);
    setSelectedPassenger(null);
    setPassengerStatus('');
    setConflictPassenger(null);
    setAdditionalPassengers([]);
    setAdditionalPassengerSearchData({ name: '', surname: '', dni: '' });
    setAdditionalPassengerSuggestions([]);
    setShowAdditionalPassengerSuggestions(false);
    setBookingItinerary([]);
    setBookingAccommodations([]);
    setBookingPayments([]);
    setBookingCollections([]);
    setBookingTravelInformations([]);
    setEditingId(null);
    setShowForm(false);
  };

  // --- Search & Pagination ---
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleSort = (field) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);
    setCurrentPage(0);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const buildSearchParam = () => {
    if (!searchField || !searchValue) return '';

    // Manejar campos de estado que tienen su propia lógica de filtrado en el frontend
    if (searchField === 'paymentStatus' || searchField === 'collectionStatus') {
      return `${searchField}=${searchValue}`;
    }

    const fieldMap = {
      'id': 'id.equals',
      'passengerName': 'passenger.name.contains',
      'passengerSurname': 'passenger.surname.contains',
    };

    const apiField = fieldMap[searchField];
    return apiField ? `${apiField}=${encodeURIComponent(searchValue.trim())}` : '';
  };

  const handleSearchFieldChange = (e) => {
    setSearchField(e.target.value);
    setSearchValue('');
    setCurrentPage(0);
    if (!e.target.value) {
      setLoading(true);
      const sortParam = sortField ? `${sortField},${sortDirection}` : '';
      fetchBookings(0, sortParam);
    }
  };

  const handleSearchValueChange = (e) => {
    setSearchValue(e.target.value);
    setCurrentPage(0);
  };

  useEffect(() => {
    const sortParam = `${sortField},${sortDirection}`;
    const searchParam = buildSearchParam();

    // Debounce search input to avoid excessive API calls
    const handler = setTimeout(() => {
      // currentPage is a dependency, but for new searches/sorts we want to go to page 0.
      // handleSort and handleSearch...Change already reset currentPage to 0.
      // So this effect will run with currentPage=0 for new searches/sorts.
      fetchBookings(currentPage, sortParam, searchParam);
    }, 300);

    return () => clearTimeout(handler);
  }, [currentPage, searchValue, searchField, sortField, sortDirection, fetchBookings]);

  const handleView = async (booking) => {
    setLoading(true);
    try {
      const itineraryRes = await fetch(`${API_URL}/api/booking-itineraries?bookingId.equals=${booking.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const itineraryData = itineraryRes.ok ? await itineraryRes.json() : [];
      const fullItinerary = await Promise.all(itineraryData.map(async item => {
        const destRes = await fetch(`${API_URL}/api/destinations/${item.destination.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        const destination = destRes.ok ? await destRes.json() : null;
        return { ...item, destination };
      }));

      const accommodationsRes = await fetch(`${API_URL}/api/booking-acommodations?bookingId.equals=${booking.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const accommodationsData = accommodationsRes.ok ? await accommodationsRes.json() : [];
      const fullAccommodations = await Promise.all(accommodationsData.map(async item => {
        const accRes = await fetch(`${API_URL}/api/acommodations/${item.acommodation.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
        const acommodation = accRes.ok ? await accRes.json() : null;
        return { ...item, acommodation };
      }));

      const paymentsRes = await fetch(`${API_URL}/api/booking-payments?bookingId.equals=${booking.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const paymentsData = paymentsRes.ok ? await paymentsRes.json() : [];

      const collectionsRes = await fetch(`${API_URL}/api/booking-collections?bookingId.equals=${booking.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const collectionsData = collectionsRes.ok ? await collectionsRes.json() : [];

      const travelInfoRes = await fetch(`${API_URL}/api/booking-travel-informations?bookingId.equals=${booking.id}`, { headers: { 'Authorization': `Bearer ${token}` } });
      const travelInfoData = travelInfoRes.ok ? await travelInfoRes.json() : [];

      setViewingBooking({ 
        ...booking, 
        bookingItineraries: fullItinerary, 
        bookingAccommodations: fullAccommodations,
        bookingPayments: paymentsData,
        bookingCollections: collectionsData,
        bookingTravelInformations: travelInfoData
      });
      setShowViewModal(true);
    } catch (error) {
      console.error("Error fetching details for view modal:", error);
      setViewingBooking(booking); setShowViewModal(true);
    } finally { setLoading(false); }
  };

  const getSearchPlaceholder = () => {
    switch (searchField) {
      case 'id':
        return 'Buscar por ID...';
      case 'passengerName':
        return 'Buscar por nombre...';
      case 'passengerSurname':
        return 'Buscar por apellido...';
      default:
        return 'Escriba para buscar...';
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Gestión de Reservas</h2>
        <button onClick={() => { resetForm(); setShowForm(true); }} style={styles.addButton}>
          + Agregar Reserva
        </button>
      </div>

      <div style={styles.searchContainer}>
        <div style={styles.searchField}>
          <label style={styles.searchLabel}>Buscar por:</label>
          <select value={searchField} onChange={handleSearchFieldChange} style={styles.searchSelect}>
            <option value="">Seleccionar campo</option>
            <option value="id">ID</option>
            <option value="passengerName">Nombre del Pasajero</option>
            <option value="passengerSurname">Apellido del Pasajero</option>
            <option value="paymentStatus">Estado de Pagos</option>
            <option value="collectionStatus">Estado de Cobros</option>
          </select>
        </div>
        {searchField && (
          <div style={styles.searchField}>
            <label style={styles.searchLabel}>Valor:</label>
            {searchField === 'paymentStatus' ? (
              <select value={searchValue} onChange={handleSearchValueChange} style={styles.searchInput}>
                <option value="">Todos</option>
                <option value="Pagado">Pagado</option>
                <option value="Pagado Parcial">Pagado Parcial</option>
                <option value="Pendiente">Pendiente</option>
                <option value="Sin Pagos">Sin Pagos</option>
              </select>
            ) : searchField === 'collectionStatus' ? (
              <select value={searchValue} onChange={handleSearchValueChange} style={styles.searchInput}>
                <option value="">Todos</option>
                <option value="Cobrado">Cobrado</option>
                <option value="Señado">Señado</option>
                <option value="A Cobrar">A Cobrar</option>
                <option value="N/A">N/A</option>
              </select>
            ) : (
              <input type="text" value={searchValue} onChange={handleSearchValueChange} placeholder={getSearchPlaceholder()} style={styles.searchInput} />
            )}
          </div>
        )}
        {(searchField || searchValue) && (
          <button onClick={() => { setSearchField(''); setSearchValue(''); setCurrentPage(0); setLoading(true); fetchBookings(0, `${sortField},${sortDirection}`); }} style={styles.clearButton}>Limpiar</button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} style={styles.form}>
          <h3 style={styles.formTitle}>{editingId ? 'Editar Reserva' : 'Nueva Reserva'}</h3>
          
          <div style={styles.passengerSection}>
            <h4 style={styles.sectionTitle}>Datos del Pasajero Titular</h4>
            {passengerStatus === 'existing' && <div style={styles.statusMessage}>✅ Pasajero existente: {selectedPassenger?.name} {selectedPassenger?.surname}</div>}
            {passengerStatus === 'new' && <div style={styles.statusMessageNew}>➕ Se creará un nuevo pasajero con los datos ingresados.</div>}
            {passengerStatus === 'conflict' && <div style={styles.statusMessageError}>⚠️ CONFLICTO: El DNI ya pertenece a {conflictPassenger?.name} {conflictPassenger?.surname}.</div>}

            <div style={styles.passengerGrid}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Apellido *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="surname" value={passengerData.surname} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('surname')} style={styles.input} required autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'surname' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Nombre *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="name" value={passengerData.name} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('name')} style={styles.input} required autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'name' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>DNI *</label>
                <div style={styles.autocompleteContainer}>
                  <input name="dni" value={passengerData.dni} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('dni')} style={styles.input} required autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'dni' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Teléfono</label>
                <div style={styles.autocompleteContainer}>
                  <input name="phoneNumber" value={passengerData.phoneNumber} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('phoneNumber')} style={styles.input} autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'phoneNumber' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - Tel: {p.phoneNumber}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Email</label>
                <div style={styles.autocompleteContainer}>
                  <input name="email" type="email" value={passengerData.email} onChange={handlePassengerInputChange} onBlur={handlePassengerInputBlur} onFocus={() => handlePassengerInputFocus('email')} style={styles.input} autoComplete="off" />
                  {showSuggestions && suggestions.length > 0 && currentSearchField === 'email' && (
                    <div style={styles.suggestionsDropdown}>
                      {suggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => selectSuggestion(p)}>{p.surname} {p.name} - Email: {p.email}</div>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div style={styles.additionalPassengersSection}>
            <h4 style={styles.sectionTitle}>Pasajeros Adicionales</h4>
            <div style={styles.passengerGrid}>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Buscar por Apellido</label>
                <div style={styles.autocompleteContainer}>
                  <input name="surname" value={additionalPassengerSearchData.surname} onChange={handleAdditionalPassengerInputChange} onBlur={handleAdditionalPassengerInputBlur} onFocus={() => handleAdditionalPassengerInputFocus('surname')} style={styles.input} autoComplete="off" placeholder="Apellido del pasajero..."/>
                  {showAdditionalPassengerSuggestions && additionalPassengerSuggestions.length > 0 && currentAdditionalSearchField === 'surname' && (
                    <div style={styles.suggestionsDropdown}>
                      {additionalPassengerSuggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => addAdditionalPassenger(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Buscar por Nombre</label>
                <div style={styles.autocompleteContainer}>
                  <input name="name" value={additionalPassengerSearchData.name} onChange={handleAdditionalPassengerInputChange} onBlur={handleAdditionalPassengerInputBlur} onFocus={() => handleAdditionalPassengerInputFocus('name')} style={styles.input} autoComplete="off" placeholder="Nombre del pasajero..."/>
                  {showAdditionalPassengerSuggestions && additionalPassengerSuggestions.length > 0 && currentAdditionalSearchField === 'name' && (
                    <div style={styles.suggestionsDropdown}>
                      {additionalPassengerSuggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => addAdditionalPassenger(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
              <div style={styles.fieldContainer}>
                <label style={styles.label}>Buscar por DNI</label>
                <div style={styles.autocompleteContainer}>
                  <input name="dni" value={additionalPassengerSearchData.dni} onChange={handleAdditionalPassengerInputChange} onBlur={handleAdditionalPassengerInputBlur} onFocus={() => handleAdditionalPassengerInputFocus('dni')} style={styles.input} autoComplete="off" placeholder="DNI del pasajero..."/>
                  {showAdditionalPassengerSuggestions && additionalPassengerSuggestions.length > 0 && currentAdditionalSearchField === 'dni' && (
                    <div style={styles.suggestionsDropdown}>
                      {additionalPassengerSuggestions.map(p => <div key={p.id} style={styles.suggestionItem} onClick={() => addAdditionalPassenger(p)}>{p.surname} {p.name} - DNI: {p.dni}</div>)}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <ul style={styles.additionalPassengerList}>
              {additionalPassengers.map(({ passenger }) => (
                <li key={passenger.id} style={styles.additionalPassengerItem}>
                  <span>{passenger.surname} {passenger.name} (DNI: {passenger.dni})</span>
                  <button type="button" onClick={() => removeAdditionalPassenger(passenger.id)} style={styles.removePassengerButton}>Quitar</button>
                </li>
              ))}
            </ul>
          </div>

          <Itinerary itinerary={bookingItinerary} setItinerary={setBookingItinerary} />

          <div style={styles.accommodationsSection}>
            <h4 style={styles.sectionTitle}>Alojamientos de la Reserva</h4>
            {bookingAccommodations.map((acc, index) => (
              <div key={index} style={styles.accommodationRow}>
                <div style={{...styles.fieldContainer, flex: 2, minWidth: '200px', position: 'relative' }}>
                  <label style={styles.subLabel}>Alojamiento</label>
                  <input type="text" placeholder="Buscar..." value={acc.searchTerm} onChange={(e) => handleBookingAccommodationSearch(index, e.target.value)} onFocus={() => setActiveAccSearchIndex(index)} onBlur={() => setTimeout(() => setActiveAccSearchIndex(null), 200)} style={styles.subInput} autoComplete="off" />
                  {activeAccSearchIndex === index && (
                    <div style={styles.suggestionsDropdown}>
                      {bookingItinerary.length === 0 ? (
                        <div style={styles.suggestionItem}>Agregue destinos al itinerario para ver alojamientos.</div>
                      ) : (
                        availableBookingAccommodations.filter(availAcc => 
                          acc.searchTerm ? availAcc.acommodationName.toLowerCase().includes(acc.searchTerm.toLowerCase()) : true
                        ).length > 0 ? (
                          availableBookingAccommodations
                            .filter(availAcc => acc.searchTerm ? availAcc.acommodationName.toLowerCase().includes(acc.searchTerm.toLowerCase()) : true)
                            .slice(0, 10)
                            .map(sugg => (
                              <div key={sugg.id} style={styles.suggestionItem} onClick={() => selectBookingAccommodation(index, sugg)}>{sugg.acommodationName}</div>
                            ))
                        ) : (
                          <div style={styles.suggestionItem}>No se encontraron alojamientos.</div>
                        )
                      )}
                    </div>
                  )}
                </div>
                <div style={{...styles.fieldContainer, flex: 1, minWidth: '140px' }}>
                  <label style={styles.subLabel}>Llegada</label>
                  <input type="date" value={acc.arriveDate || ''} onChange={(e) => handleBookingAccommodationChange(index, 'arriveDate', e.target.value)} style={styles.subInput} />
                </div>
                <div style={{...styles.fieldContainer, flex: 1, minWidth: '140px' }}>
                  <label style={styles.subLabel}>Salida</label>
                  <input type="date" value={acc.leaveDate || ''} onChange={(e) => handleBookingAccommodationChange(index, 'leaveDate', e.target.value)} style={styles.subInput} />
                </div>
                <div style={{...styles.fieldContainer, flex: 2.5, minWidth: '240px' }}>
                  <label style={styles.subLabel}>Comentarios</label>
                  <textarea placeholder="Comentarios..." value={acc.accomodationComments || ''} onChange={(e) => handleBookingAccommodationChange(index, 'accomodationComments', e.target.value)} style={{...styles.subInput, minHeight: '42px', resize: 'vertical'}} />
                </div>
                <button type="button" onClick={() => removeBookingAccommodation(index)} style={styles.removeSubItemButton}>×</button>
              </div>
            ))}
            <button type="button" onClick={addBookingAccommodation} style={styles.addSubItemButton}>+ Agregar Alojamiento</button>
          </div>

          <div style={styles.paymentsSection}>
            <h4 style={styles.sectionTitle}>Pagos a Proveedores</h4>
            {bookingPayments.map((payment, index) => (
              <div key={index} style={styles.financialRow}>
                <input type="text" placeholder="Proveedor" value={payment.supplier || ''} onChange={(e) => handleBookingPaymentChange(index, 'supplier', e.target.value)} style={styles.subInput} />
                <input type="text" placeholder="Cód. Reserva" value={payment.reservationCode || ''} onChange={(e) => handleBookingPaymentChange(index, 'reservationCode', e.target.value)} style={styles.subInput} />
                <input type="number" placeholder="Monto" value={payment.amount || ''} onChange={(e) => handleBookingPaymentChange(index, 'amount', e.target.value === '' ? '' : parseFloat(e.target.value))} style={styles.subInput} />
                <input type="text" placeholder="Nº Factura" value={payment.invoiceNumber || ''} onChange={(e) => handleBookingPaymentChange(index, 'invoiceNumber', e.target.value)} style={styles.subInput} />
                <div style={styles.fieldContainer}>
                  <label style={styles.subLabel}>Vencimiento</label>
                  <input type="date" value={payment.expirationDate || ''} onChange={(e) => handleBookingPaymentChange(index, 'expirationDate', e.target.value)} style={styles.subInput} />
                </div>
                <label style={styles.financialCheckboxLabel}>
                  <input type="checkbox" checked={payment.paid} onChange={(e) => handleBookingPaymentChange(index, 'paid', e.target.checked)} style={{ transform: 'scale(1.5)', marginRight: '10px' }} /> 
                  Pagado
                </label>
                <button type="button" onClick={() => removeBookingPayment(index)} style={styles.removeSubItemButton}>×</button>
              </div>
            ))}
            <button type="button" onClick={addBookingPayment} style={styles.addSubItemButton}>+ Agregar Pago</button>
          </div>

          <div style={styles.collectionsSection}>
            <h4 style={styles.sectionTitle}>Cobros a Clientes</h4>
            {bookingCollections.map((collection, index) => (
              <div key={index} style={styles.financialRow}>
                <select value={collection.type} onChange={(e) => handleBookingCollectionChange(index, 'type', e.target.value)} style={styles.subInput}>
                  <option value="Seño">Seño</option>
                  <option value="Refuerzo">Refuerzo</option>
                  <option value="Saldo">Saldo</option>
                </select>
                <input type="number" placeholder="Monto" value={collection.amount || ''} onChange={(e) => handleBookingCollectionChange(index, 'amount', e.target.value === '' ? '' : parseFloat(e.target.value))} style={styles.subInput} />
                <input type="text" placeholder="Nº Recibo" value={collection.reciptNumber || ''} onChange={(e) => handleBookingCollectionChange(index, 'reciptNumber', e.target.value)} style={styles.subInput} />
                <input type="text" placeholder="Nº Factura" value={collection.billNumber || ''} onChange={(e) => handleBookingCollectionChange(index, 'billNumber', e.target.value)} style={styles.subInput} />
                <input type="date" placeholder="Fecha Cobro" value={collection.collectionDate || ''} onChange={(e) => handleBookingCollectionChange(index, 'collectionDate', e.target.value)} style={styles.subInput} />
                <button type="button" onClick={() => removeBookingCollection(index)} style={styles.removeSubItemButton}>×</button>
              </div>
            ))}
            <button type="button" onClick={addBookingCollection} style={styles.addSubItemButton}>+ Agregar Cobro</button>
          </div>

          <div style={styles.travelInfoSection}>
            <h4 style={styles.sectionTitle}>Información de Vuelos / Viajes</h4>
            {bookingTravelInformations.map((info, index) => (
              <div key={index} style={styles.accommodationRow}>
                <div style={{...styles.fieldContainer, flex: 1.5, minWidth: '180px' }}>
                  <label style={styles.subLabel}>Empresa</label>
                  <input type="text" placeholder="" value={info.travelCompany || ''} onChange={(e) => handleBookingTravelInfoChange(index, 'travelCompany', e.target.value)} style={styles.subInput} />
                </div>
                <div style={{...styles.fieldContainer, flex: 0.8, minWidth: '90px' }}>
                  <label style={styles.subLabel}>Aeropuerto Salida</label>
                  <input type="text" maxLength="3" placeholder="" value={info.departureAirport || ''} onChange={(e) => handleBookingTravelInfoChange(index, 'departureAirport', e.target.value)} style={styles.subInput} />
                </div>
                <div style={{...styles.fieldContainer, flex: 0.8, minWidth: '90px' }}>
                  <label style={styles.subLabel}>Aeropuerto Llegada</label>
                  <input type="text" maxLength="3" placeholder="" value={info.arrivalAirport || ''} onChange={(e) => handleBookingTravelInfoChange(index, 'arrivalAirport', e.target.value)} style={styles.subInput} />
                </div>
                <div style={{...styles.fieldContainer, flex: 1, minWidth: '110px' }}>
                  <label style={styles.subLabel}>Cód. Reserva</label>
                  <input type="text" placeholder="" value={info.reservationCode || ''} onChange={(e) => handleBookingTravelInfoChange(index, 'reservationCode', e.target.value)} style={styles.subInput} />
                </div>
                <div style={{...styles.fieldContainer, flex: 1.5, minWidth: '180px' }}>
                  <label style={styles.subLabel}>Fecha y Hora Salida</label>
                  <input type="datetime-local" value={toLocalISO(info.departureTime)} onChange={(e) => handleBookingTravelInfoChange(index, 'departureTime', e.target.value)} style={styles.subInput} />
                </div>
                <div style={{...styles.fieldContainer, width: '120px' }}>
                  <label style={styles.subLabel}>Tiempo de Viaje</label>
                  <input 
                    type="time" 
                    value={`${String(info.travelTimeHours || 0).padStart(2, '0')}:${String(info.travelTimeMinutes || 0).padStart(2, '0')}`} 
                    onChange={(e) => {
                      const val = e.target.value;
                      const [h, m] = val ? val.split(':') : ['', ''];
                      const updated = [...bookingTravelInformations];
                      updated[index].travelTimeHours = h;
                      updated[index].travelTimeMinutes = m;
                      setBookingTravelInformations(updated);
                    }} 
                    style={styles.subInput} 
                  />
                </div>
                <div style={{...styles.fieldContainer, width: '70px', alignItems: 'center' }}>
                  <label style={styles.subLabel}>Escala?</label>
                  <input type="checkbox" checked={info.travelStopover || false} onChange={(e) => handleBookingTravelInfoChange(index, 'travelStopover', e.target.checked)} style={{transform: 'scale(1.3)', marginTop: '10px'}} />
                </div>
                {info.travelStopover && (
                  <>
                    <div style={{...styles.fieldContainer, width: '120px' }}>
                      <label style={styles.subLabel}>Tiempo de Espera</label>
                      <input 
                        type="time" 
                        value={`${String(info.stopoverDurationHours || 0).padStart(2, '0')}:${String(info.stopoverDurationMinutes || 0).padStart(2, '0')}`} 
                        onChange={(e) => {
                          const val = e.target.value;
                          const [h, m] = val ? val.split(':') : ['', ''];
                          const updated = [...bookingTravelInformations];
                          updated[index].stopoverDurationHours = h;
                          updated[index].stopoverDurationMinutes = m;
                          setBookingTravelInformations(updated);
                        }} 
                        style={styles.subInput} 
                      />
                    </div>
                    <div style={{...styles.fieldContainer, width: '150px', alignItems: 'center' }}>
                      <label style={styles.subLabel}>Cambio de aeropuerto?</label>
                      <input type="checkbox" checked={info.airportChange || false} onChange={(e) => handleBookingTravelInfoChange(index, 'airportChange', e.target.checked)} style={{transform: 'scale(1.3)', marginTop: '10px'}} />
                    </div>
                  </>
                )}
                <button type="button" onClick={() => removeBookingTravelInfo(index)} style={styles.removeSubItemButton}>×</button>
              </div>
            ))}
            <button type="button" onClick={addBookingTravelInfo} style={styles.addSubItemButton}>+ Agregar Info de Vuelo</button>
          </div>

          <div style={styles.formGrid}>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>ID Presupuesto (Opcional)</label>
              <input name="budgetId" type="number" value={formData.budget.id || ''} onChange={handleInputChange} style={styles.input} placeholder="ID del presupuesto" />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Número de Pasajeros</label>
              <input name="numberPassengers" type="number" value={formData.numberPassengers} onChange={handleInputChange} style={styles.input} required />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Precio Consumidor</label>
              <input name="consumerPrice" type="number" value={formData.consumerPrice} onChange={handleInputChange} style={styles.input} required />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Tipo de Viaje</label>
              <select name="typeTrip" value={formData.typeTrip} onChange={handleInputChange} style={styles.input}>
                <option value="">Seleccionar tipo</option>
                <option value="Aereo">Aereo</option>
                <option value="Terrestre">Terrestre</option>
                <option value="Maritimo">Maritimo</option>
              </select>
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Empresa de Viaje</label>
              <input name="travelCompany" value={formData.travelCompany} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Equipaje</label>
              <input name="luggage" value={formData.luggage} onChange={handleInputChange} style={styles.input} />
            </div>
            <div style={styles.fieldContainer}>
              <label style={styles.label}>Origen</label>
              <input name="origin" value={formData.origin} onChange={handleInputChange} style={styles.input} />
            </div>
          </div>

          <div style={styles.checkboxGrid}>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" name="transfer" checked={formData.transfer} onChange={handleInputChange} style={styles.checkbox} />
                Transfer
              </label>
              {formData.transfer && <textarea name="transferComments" placeholder="Comentarios Transfer" value={formData.transferComments} onChange={handleInputChange} style={styles.textarea} />}
            </div>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" name="carRental" checked={formData.carRental} onChange={handleInputChange} style={styles.checkbox} />
                Alquiler Auto
              </label>
              {formData.carRental && <textarea name="carRentalComments" placeholder="Comentarios Auto" value={formData.carRentalComments} onChange={handleInputChange} style={styles.textarea} />}
            </div>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" name="excursion" checked={formData.excursion} onChange={handleInputChange} style={styles.checkbox} />
                Excursión
              </label>
              {formData.excursion && <textarea name="excursionComments" placeholder="Comentarios Excursión" value={formData.excursionComments} onChange={handleInputChange} style={styles.textarea} />}
            </div>
            <div style={styles.checkboxGroup}>
              <label style={styles.checkboxLabel}>
                <input type="checkbox" name="travelInsurance" checked={formData.travelInsurance} onChange={handleInputChange} style={styles.checkbox} />
                Seguro Viaje
              </label>
              {formData.travelInsurance && <textarea name="travelInsuranceComments" placeholder="Comentarios Seguro" value={formData.travelInsuranceComments} onChange={handleInputChange} style={styles.textarea} />}
            </div>
          </div>

          <div style={styles.fieldContainer}>
            <label style={styles.label}>Comentarios Generales de la Reserva</label>
            <textarea name="bookingComments" value={formData.bookingComments} onChange={handleInputChange} style={styles.textarea} />
          </div>

          <div style={styles.formButtons}>
            <button type="button" onClick={resetForm} style={styles.cancelButton}>Cancelar</button>
            <button type="submit" style={styles.saveButton}>Guardar</button>
          </div>
        </form>
      )}

      <div style={styles.tableContainer}>
        {loading ? (
          <div style={styles.emptyState}>Cargando reservas...</div>
        ) : bookings.length === 0 ? (
          <div style={styles.emptyState}>No hay reservas registradas.</div>
        ) : (
          <>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.sortableHeader} onClick={() => handleSort('id')}>ID{getSortArrow('id')}</th>
                  <th style={styles.sortableHeader} onClick={() => handleSort('passenger.name')}>Pasajero{getSortArrow('passenger.name')}</th>
                  <th style={styles.tableHeader}>Estado de Pagos</th>
                  <th style={styles.tableHeader}>Estado de Cobros</th>
                  <th style={styles.tableHeader}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map(booking => {
                  const paymentStatus = getPaymentStatus(booking);
                  const collectionStatus = getCollectionStatus(booking);
                  return (
                    <tr key={booking.id}>
                      <td style={styles.tableCell}>{booking.id}</td>
                      <td style={styles.tableCell}>{booking.passenger ? `${booking.passenger.surname} ${booking.passenger.name}` : 'N/A'}</td>
                      <td style={styles.tableCell}>
                        <span style={{...styles.statusBadge, ...paymentStatus.style}}>{paymentStatus.text}</span>
                      </td>
                      <td style={styles.tableCell}>
                        <span style={{...styles.statusBadge, ...collectionStatus.style}}>{collectionStatus.text}</span>
                      </td>
                      <td style={styles.tableCell}>
                        <div style={styles.actionButtons}>
                          <button onClick={() => handleView(booking)} style={styles.viewButton}>Ver</button>
                          <button onClick={() => handleEdit(booking)} style={styles.editButton}>Editar</button>
                          <button onClick={() => handleDelete(booking.id)} style={styles.deleteButton}>Eliminar</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div style={styles.pagination}>
              <div style={styles.paginationInfo}>Mostrando {currentPage * pageSize + 1} - {Math.min((currentPage + 1) * pageSize, totalElements)} de {totalElements} reservas</div>
              <div style={styles.paginationControls}>
                <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} style={currentPage === 0 ? styles.paginationButtonDisabled : styles.paginationButton}>Anterior</button>
                <span style={styles.pageInfo}>Página {currentPage + 1} de {totalPages}</span>
                <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} style={currentPage >= totalPages - 1 ? styles.paginationButtonDisabled : styles.paginationButton}>Siguiente</button>
              </div>
            </div>
          </>
        )}
      </div>

      {showViewModal && viewingBooking && (
        <div style={styles.modalOverlay}>
          <div style={styles.viewModal}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>Información de la Reserva</h3>
              <button onClick={() => setShowViewModal(false)} style={styles.closeButton} type="button">×</button>
            </div>
            <div style={styles.viewContent}>
              <div style={styles.viewGrid}>
                <div style={styles.viewRow}><strong>ID Reserva:</strong> {viewingBooking.id}</div>
                <div style={styles.viewRow}><strong>Pasajero:</strong> {viewingBooking.passenger ? `${viewingBooking.passenger.surname} ${viewingBooking.passenger.name}` : 'N/A'}</div>
                <div style={styles.viewRow}><strong>Empresa:</strong> {viewingBooking.travelCompany}</div>
                <div style={styles.viewRow}><strong>Tipo Viaje:</strong> {viewingBooking.typeTrip}</div>
                <div style={styles.viewRow}><strong>Precio:</strong> ${viewingBooking.consumerPrice}</div>
                <div style={styles.viewRow}><strong>Pasajeros:</strong> {viewingBooking.numberPassengers}</div>
                <div style={styles.viewRow}><strong>Origen:</strong> {viewingBooking.origin}</div>
                <div style={styles.viewRow}><strong>Equipaje:</strong> {viewingBooking.luggage}</div>
                <div style={styles.viewRow}><strong>Transfer:</strong> {viewingBooking.transfer ? 'Sí' : 'No'}</div>
                <div style={styles.viewRow}><strong>Alq. Auto:</strong> {viewingBooking.carRental ? 'Sí' : 'No'}</div>
                <div style={styles.viewRow}><strong>Excursión:</strong> {viewingBooking.excursion ? 'Sí' : 'No'}</div>
                <div style={styles.viewRow}><strong>Seguro:</strong> {viewingBooking.travelInsurance ? 'Sí' : 'No'}</div>
                <div style={styles.viewRow}><strong>ID Presupuesto:</strong> {viewingBooking.budget?.id || 'N/A'}</div>
              </div>
              <div style={styles.viewRow}><strong>Comentarios:</strong> {viewingBooking.bookingComments}</div>

              {viewingBooking.bookingItineraries && viewingBooking.bookingItineraries.length > 0 && (
                <div style={styles.viewSection}>
                  <h4 style={styles.viewSectionTitle}>Itinerario</h4>
                  <table style={styles.viewTable}>
                    <thead><tr><th style={styles.viewTableHeader}>Destino</th><th style={styles.viewTableHeader}>Salida</th><th style={styles.viewTableHeader}>Regreso</th></tr></thead>
                    <tbody>
                      {viewingBooking.bookingItineraries.map(item => (
                        <tr key={item.id}>
                          <td style={styles.viewTableCell}>{item.destination ? `${item.destination.destinationName} - ${item.destination.country}` : 'N/A'}</td>
                          <td style={styles.viewTableCell}>{formatDate(item.departureDate)}</td>
                          <td style={styles.viewTableCell}>{formatDate(item.returnDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewingBooking.bookingAccommodations && viewingBooking.bookingAccommodations.length > 0 && (
                <div style={styles.viewSection}>
                  <h4 style={styles.viewSectionTitle}>Alojamientos</h4>
                   <table style={styles.viewTable}>
                    <thead><tr><th style={styles.viewTableHeader}>Alojamiento</th><th style={styles.viewTableHeader}>Llegada</th><th style={styles.viewTableHeader}>Salida</th><th style={styles.viewTableHeader}>Comentarios</th></tr></thead>
                    <tbody>
                      {viewingBooking.bookingAccommodations.map(item => (
                        <tr key={item.id}>
                          <td style={styles.viewTableCell}>{item.acommodation?.acommodationName || 'N/A'}</td>
                          <td style={styles.viewTableCell}>{formatDate(item.arriveDate)}</td>
                          <td style={styles.viewTableCell}>{formatDate(item.leaveDate)}</td>
                          <td style={styles.viewTableCell}>{item.accomodationComments}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewingBooking.bookingPayments && viewingBooking.bookingPayments.length > 0 && (
                <div style={styles.viewSection}>
                  <h4 style={styles.viewSectionTitle}>Pagos a Proveedores</h4>
                   <table style={styles.viewTable}>
                    <thead><tr>
                      <th style={styles.viewTableHeader}>Proveedor</th><th style={styles.viewTableHeader}>Monto</th>
                      <th style={styles.viewTableHeader}>Vencimiento</th><th style={styles.viewTableHeader}>Pagado</th>
                    </tr></thead>
                    <tbody>
                      {viewingBooking.bookingPayments.map(item => (
                        <tr key={item.id}>
                          <td style={styles.viewTableCell}>{item.supplier}</td>
                          <td style={styles.viewTableCell}>${item.amount}</td>
                          <td style={styles.viewTableCell}>{formatDate(item.expirationDate)}</td>
                          <td style={styles.viewTableCell}>{item.paid ? 'Sí' : 'No'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewingBooking.bookingCollections && viewingBooking.bookingCollections.length > 0 && (
                <div style={styles.viewSection}>
                  <h4 style={styles.viewSectionTitle}>Cobros a Clientes</h4>
                   <table style={styles.viewTable}>
                    <thead><tr>
                      <th style={styles.viewTableHeader}>Tipo</th><th style={styles.viewTableHeader}>Monto</th>
                      <th style={styles.viewTableHeader}>Fecha</th><th style={styles.viewTableHeader}>Recibo</th>
                    </tr></thead>
                    <tbody>
                      {viewingBooking.bookingCollections.map(item => (
                        <tr key={item.id}>
                          <td style={styles.viewTableCell}>{item.type}</td>
                          <td style={styles.viewTableCell}>${item.amount}</td>
                          <td style={styles.viewTableCell}>{formatDate(item.collectionDate)}</td>
                          <td style={styles.viewTableCell}>{item.reciptNumber}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {viewingBooking.bookingTravelInformations && viewingBooking.bookingTravelInformations.length > 0 && (
                <div style={styles.viewSection}>
                  <h4 style={styles.viewSectionTitle}>Información de Vuelos / Viajes</h4>
                  <table style={styles.viewTable}>
                    <thead>
                      <tr>
                        <th style={styles.viewTableHeader}>Empresa</th>
                        <th style={styles.viewTableHeader}>Ruta</th>
                        <th style={styles.viewTableHeader}>Código</th>
                        <th style={styles.viewTableHeader}>Salida</th>
                        <th style={styles.viewTableHeader}>Duración</th>
                        <th style={styles.viewTableHeader}>Escala</th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewingBooking.bookingTravelInformations.map(item => {
                        const travelDur = parseIsoDuration(item.travelTime);
                        const stopDur = parseIsoDuration(item.stopoverDuration);
                        return (
                          <tr key={item.id}>
                            <td style={styles.viewTableCell}>{item.travelCompany}</td>
                            <td style={styles.viewTableCell}>{item.departureAirport} ➔ {item.arrivalAirport}</td>
                            <td style={styles.viewTableCell}>{item.reservationCode}</td>
                            <td style={styles.viewTableCell}>{item.departureTime ? new Date(item.departureTime).toLocaleString('es-AR') : 'N/A'}</td>
                            <td style={styles.viewTableCell}>{travelDur.hours}h {travelDur.minutes}m</td>
                            <td style={styles.viewTableCell}>{item.travelStopover ? `${stopDur.hours}h ${stopDur.minutes}m ${item.airportChange ? '(Cambio Aerop.)' : ''}` : 'No'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
            <div style={styles.modalButtons}>
              <button onClick={() => setShowViewModal(false)} style={styles.modalCancelButton}>Cerrar</button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <h3 style={styles.modalTitle}>Confirmar Eliminación</h3>
            <p style={styles.modalText}>¿Estás seguro de que deseas eliminar esta reserva?</p>
            <div style={styles.modalButtons}>
              <button onClick={() => setShowDeleteModal(false)} style={styles.modalCancelButton}>Cancelar</button>
              <button onClick={confirmDelete} style={styles.modalConfirmButton}>Eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '5px', maxWidth: '100%', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
  title: { fontSize: '36px', fontWeight: 'bold', color: '#333', margin: 0 },
  addButton: { backgroundColor: '#ff9f43', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '8px', fontSize: '20px', fontWeight: '500', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' },
  form: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', marginBottom: '30px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  formTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '20px', color: '#333' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '15px', marginBottom: '25px' },
  fieldContainer: { display: 'flex', flexDirection: 'column' },
  label: { fontSize: '16px', fontWeight: 'bold', color: '#495057', marginBottom: '4px' },
  input: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff' },
  textarea: { padding: '16px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '18px', backgroundColor: '#fff', minHeight: '80px', resize: 'vertical', marginTop: '5px' },
  formButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  saveButton: { backgroundColor: '#28a745', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  cancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  tableContainer: { backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' },
  table: { width: '100%', borderCollapse: 'collapse' },
  tableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', fontSize: '18px' },
  sortableHeader: { backgroundColor: '#f8f9fa', fontWeight: '600', padding: '20px 40px', textAlign: 'left', borderBottom: '2px solid #dee2e6', color: '#495057', cursor: 'pointer', userSelect: 'none' },
  tableCell: { padding: '20px 40px', borderBottom: '1px solid #dee2e6', fontSize: '18px' },
  actionButtons: { display: 'flex', gap: '8px', whiteSpace: 'nowrap' },
  viewButton: { backgroundColor: '#28a745', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  editButton: { backgroundColor: '#17a2b8', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  deleteButton: { backgroundColor: '#dc3545', color: 'white', padding: '10px 20px', border: 'none', borderRadius: '4px', fontSize: '16px', cursor: 'pointer' },
  emptyState: { textAlign: 'center', padding: '40px', color: '#6c757d', fontSize: '20px' },
  pagination: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px', borderTop: '1px solid #dee2e6', backgroundColor: '#f8f9fa' },
  paginationInfo: { fontSize: '18px', color: '#6c757d' },
  paginationControls: { display: 'flex', alignItems: 'center', gap: '15px' },
  paginationButton: { padding: '12px 24px', backgroundColor: '#ff9f43', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '18px' },
  paginationButtonDisabled: { padding: '12px 24px', backgroundColor: '#e9ecef', color: '#6c757d', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontSize: '18px' },
  pageInfo: { fontSize: '18px', fontWeight: '500', color: '#495057' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 },
  modal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '400px', width: '90%' },
  viewModal: { backgroundColor: 'white', padding: '30px', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)', maxWidth: '800px', width: '90%', maxHeight: '80vh', overflowY: 'auto' },
  modalTitle: { fontSize: '28px', fontWeight: '600', marginBottom: '15px', color: '#333', textAlign: 'center' },
  modalText: { fontSize: '20px', color: '#495057', marginBottom: '25px', textAlign: 'center', lineHeight: '1.5' },
  modalButtons: { display: 'flex', gap: '12px', justifyContent: 'center' },
  modalCancelButton: { backgroundColor: '#6c757d', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  modalConfirmButton: { backgroundColor: '#dc3545', color: 'white', padding: '16px 32px', border: 'none', borderRadius: '6px', fontSize: '18px', fontWeight: '500', cursor: 'pointer' },
  viewContent: { marginBottom: '25px' },
  viewRow: { padding: '12px 0', borderBottom: '1px solid #e9ecef', fontSize: '20px' },
  viewGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '15px' },
  passengerSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' },
  sectionTitle: { fontSize: '20px', fontWeight: '600', color: '#495057', marginBottom: '15px', borderBottom: '2px solid #ff9f43', paddingBottom: '5px' },
  passengerGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' },
  autocompleteContainer: { position: 'relative' },
  additionalPassengersSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #e0e0e0' },
  additionalPassengerList: { listStyle: 'none', padding: 0, marginTop: '15px' },
  additionalPassengerItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', border: '1px solid #ddd', borderRadius: '4px', marginBottom: '5px', backgroundColor: '#fff' },
  removePassengerButton: { backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer' },
  paymentsSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#e9f7ef', borderRadius: '8px', border: '1px solid #d1e7dd' },
  travelInfoSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#fff3e0', borderRadius: '8px', border: '1px solid #ffe0b2' },
  collectionsSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffeeba' },
  financialRow: { display: 'flex', gap: '10px', alignItems: 'flex-end', marginBottom: '15px', flexWrap: 'wrap' },
  financialCheckboxLabel: { 
    display: 'flex', 
    alignItems: 'center', 
    justifyContent: 'center',
    fontSize: '14px', 
    fontWeight: '600',
    whiteSpace: 'nowrap',
    backgroundColor: '#e9ecef',
    padding: '0 15px',
    borderRadius: '6px',
    cursor: 'pointer',
    height: '42px'
  },
  accommodationsSection: { marginBottom: '30px', padding: '20px', backgroundColor: '#f0f8ff', borderRadius: '8px', border: '1px solid #e0e0e0' },
  accommodationRow: { 
    border: '1px solid #e0e0e0', 
    borderRadius: '8px', 
    padding: '15px', 
    marginBottom: '15px',
    display: 'flex',
    rowGap: '15px',
    columnGap: '30px',
    alignItems: 'flex-end',
    flexWrap: 'wrap'
  },
  subLabel: { fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '4px' },
  subInput: { padding: '10px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '14px', width: '100%' },
  removeSubItemButton: { 
    backgroundColor: '#dc3545', color: 'white', border: 'none', 
    width: '42px', height: '42px', 
    borderRadius: '6px', cursor: 'pointer', fontSize: '18px', 
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0
  },
  addSubItemButton: { backgroundColor: '#28a745', color: 'white', border: 'none', padding: '8px 15px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', marginTop: '10px' },
  viewSection: { marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #eee' },
  viewSectionTitle: { fontSize: '22px', fontWeight: '600', color: '#495057', marginBottom: '10px' },
  viewTable: { width: '100%', borderCollapse: 'collapse', marginTop: '10px' },
  viewTableHeader: { backgroundColor: '#f8f9fa', padding: '8px', textAlign: 'left', borderBottom: '1px solid #dee2e6', fontSize: '16px' },
  viewTableCell: { padding: '8px', borderBottom: '1px solid #f1f1f1', fontSize: '16px' },
  suggestionsDropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '200px', overflowY: 'auto', zIndex: 1000, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
  suggestionItem: { padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f1f1', fontSize: '16px' },
  searchContainer: { backgroundColor: 'white', padding: '20px', borderRadius: '12px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', display: 'flex', gap: '15px', alignItems: 'end', flexWrap: 'wrap' },
  searchField: { display: 'flex', flexDirection: 'column', minWidth: '200px' },
  searchLabel: { fontSize: '14px', fontWeight: '600', color: '#495057', marginBottom: '5px' },
  searchSelect: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff' },
  searchInput: { padding: '12px', border: '1px solid #ddd', borderRadius: '6px', fontSize: '16px', backgroundColor: '#fff', minWidth: '250px' },
  clearButton: { backgroundColor: '#6c757d', color: 'white', padding: '12px 20px', border: 'none', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', height: 'fit-content' },
  checkboxGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px', marginBottom: '15px' },
  checkboxGroup: { display: 'flex', flexDirection: 'column' },
  checkboxLabel: { display: 'flex', alignItems: 'center', fontSize: '16px', cursor: 'pointer', marginBottom: '5px' },
  checkbox: { marginRight: '8px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' },
  closeButton: { backgroundColor: 'transparent', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#6c757d', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%' },
  statusMessage: { backgroundColor: '#d4edda', color: '#155724', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #c3e6cb', fontSize: '16px' },
  statusMessageNew: { backgroundColor: '#fff3cd', color: '#856404', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #ffeaa7', fontSize: '16px' },
  statusMessageError: { backgroundColor: '#f8d7da', color: '#721c24', padding: '12px', borderRadius: '6px', marginBottom: '15px', border: '1px solid #f5c6cb', fontSize: '16px' },
  statusBadge: {
    padding: '5px 10px',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 'bold',
    textAlign: 'center',
    color: 'white',
    display: 'inline-block',
    minWidth: '90px'
  },
  statusBadgeGreen: {
    backgroundColor: '#28a745',
  },
  statusBadgeYellow: {
    backgroundColor: '#ffc107',
    color: '#212529'
  },
  statusBadgeRed: {
    backgroundColor: '#dc3545',
  },
  statusBadgeGray: {
    backgroundColor: '#6c757d',
  },
};

export default Bookings;